/**
 * CRM Fields Manager
 *
 * Manages cached CRM field metadata used by QueryBuilder in routing/delivery rules.
 * Features:
 * - View cached fields per entity type (Deal, Lead, Contact, Company)
 * - Refresh fields from CRM API
 * - View cache status (last updated, field count)
 * - Expand to see all fields with types
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Database,
  Clock,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Hash,
  Type,
  List,
  Calendar,
  ToggleLeft,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/format-relative';
import {
  useCrmFields,
  useCrmEntityTypes,
  useRefreshCrmFields,
} from '@/features/routing/hooks';
import type { CrmEntityType, CachedCrmField } from '@/lib/api/types';

// Field type icons
const FIELD_TYPE_ICONS: Record<string, typeof Type> = {
  string: Type,
  integer: Hash,
  double: Hash,
  boolean: ToggleLeft,
  date: Calendar,
  datetime: Calendar,
  enumeration: List,
  crm_status: List,
  crm_category: List,
  employee: List,
  crm: Database,
};

function getFieldTypeIcon(type: string) {
  return FIELD_TYPE_ICONS[type] || Type;
}

interface EntityTypeCardProps {
  entityType: CrmEntityType;
  label: string;
  onRefresh: () => void;
  isRefreshing: boolean;
}

function EntityTypeCard({
  entityType,
  label,
  onRefresh,
  isRefreshing,
}: EntityTypeCardProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [expanded, setExpanded] = useState(false);

  // Fetch fields for this entity type
  const {
    data: fieldsResponse,
    isLoading,
    error,
  } = useCrmFields({
    entityType,
    enabled: true,
  });

  const responseData = fieldsResponse?.data;
  const isSingleEntity =
    responseData && 'fields' in responseData && 'entityType' in responseData;

  const fields: CachedCrmField[] = isSingleEntity
    ? (responseData as { fields: CachedCrmField[] }).fields
    : [];
  const cacheStatus = isSingleEntity
    ? (responseData as { cacheStatus: string }).cacheStatus
    : 'empty';
  const cacheAgeMinutes = isSingleEntity
    ? (responseData as { cacheAgeMinutes?: number }).cacheAgeMinutes
    : undefined;

  // The backend reports an age, not a timestamp — turn it back into one so the
  // shared Intl helper can say "5 minutes ago" in the viewer's language.
  const formatCacheAge = (minutes?: number) =>
    minutes
      ? formatRelativeTime(new Date(Date.now() - minutes * 60_000).toISOString())
      : null;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3 bg-bg-elevated cursor-pointer',
          'hover:bg-bg-surface transition-colors'
        )}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-fg-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-fg-muted" />
          )}
          <Database className="h-4 w-4 text-primary" />
          <span className="font-medium text-fg">{label}</span>

          {/* Cache status badge */}
          {isLoading ? (
            <Badge outline className="text-xs">
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              Loading
            </Badge>
          ) : error ? (
            <Badge variant="danger" className="text-xs">
              <XCircle className="h-3 w-3 mr-1" />
              Error
            </Badge>
          ) : cacheStatus === 'cached' ? (
            <Badge variant="success" className="text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              {fields.length} {t('crmFields.fields', 'fields')}
            </Badge>
          ) : (
            <Badge variant="warning" className="text-xs">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {t('crmFields.notCached', 'Not cached')}
            </Badge>
          )}

          {/* Cache age */}
          {cacheAgeMinutes !== undefined && (
            <span className="text-xs text-fg-muted flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatCacheAge(cacheAgeMinutes)}
            </span>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRefresh();
          }}
          disabled={isRefreshing}
          className="gap-1"
        >
          <RefreshCw className={cn('h-3 w-3', isRefreshing && 'animate-spin')} />
          {isRefreshing
            ? t('crmFields.refreshing', 'Refreshing...')
            : t('crmFields.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Expanded fields list */}
      {expanded && (
        <div className="border-t border-border bg-bg-surface">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
            </div>
          ) : error ? (
            <div className="text-center py-6 px-4">
              <XCircle className="h-8 w-8 mx-auto mb-2 text-danger" />
              <p className="text-sm text-fg-muted">
                {t(
                  'settings.crmFields.loadError',
                  'Failed to load fields. Click Refresh to try again.'
                )}
              </p>
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center py-6 text-fg-muted">
              <Database className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
              <p className="text-sm">
                {t(
                  'settings.crmFields.noFields',
                  'No fields cached. Click Refresh to load fields from CRM.'
                )}
              </p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-bg-surface">
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2 font-medium text-fg-muted">
                      {t('crmFields.fieldId', 'Field ID')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-fg-muted">
                      {t('crmFields.title', 'Title')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-fg-muted">
                      {t('crmFields.type', 'Type')}
                    </th>
                    <th className="text-center px-4 py-2 font-medium text-fg-muted">
                      {t('crmFields.options', 'Options')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field) => {
                    const Icon = getFieldTypeIcon(field.type);
                    return (
                      <tr
                        key={field.fieldId}
                        className="border-b border-border-muted hover:bg-bg-muted/50"
                      >
                        <td className="px-4 py-2 font-mono text-xs text-fg">
                          {field.fieldId}
                        </td>
                        <td className="px-4 py-2 text-fg">{field.title}</td>
                        <td className="px-4 py-2">
                          <span className="flex items-center gap-1 text-fg-muted">
                            <Icon className="h-3 w-3" />
                            {field.type}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {field.enumValues && field.enumValues.length > 0 && (
                            <Badge outline className="text-xs">
                              {field.enumValues.length}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CRMFieldsManager() {
  const { t } = useTranslation(['settings', 'common']);
  const [refreshingType, setRefreshingType] = useState<CrmEntityType | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  // Fetch entity types
  const { data: entityTypesResponse, isLoading: entityTypesLoading } =
    useCrmEntityTypes();
  const entityTypes = entityTypesResponse?.data || [];

  // Refresh mutation
  const refreshMutation = useRefreshCrmFields();

  const handleRefresh = useCallback(
    async (entityType: CrmEntityType) => {
      setRefreshingType(entityType);
      setLastError(null); // Clear previous errors

      try {
        await refreshMutation.mutateAsync(entityType);
        // Success toast shown automatically by useRefreshCrmFields hook
      } catch (error) {
        // Error toast shown automatically by useRefreshCrmFields hook
        // But we also store error for persistent display
        const errorMessage =
          error instanceof Error ? error.message : t('common:common.errorUnknown');
        setLastError(errorMessage);
        console.error('crm_fields_manager.refresh_failed', {
          entityType,
          error: errorMessage,
        });
      } finally {
        setRefreshingType(null);
      }
    },
    [refreshMutation, t]
  );

  // Default entity types if API doesn't return them
  const defaultEntityTypes: Array<{ value: CrmEntityType; label: string }> = [
    { value: 'deal', label: 'Deals' },
    { value: 'lead', label: 'Leads' },
    { value: 'contact', label: 'Contacts' },
    { value: 'company', label: 'Companies' },
  ];

  const displayEntityTypes = entityTypes.length > 0 ? entityTypes : defaultEntityTypes;

  // Check if error indicates CRM not connected
  const isCrmNotConnectedError =
    lastError?.includes('CRM_INTEGRATION_NOT_FOUND') ||
    lastError?.includes(t('crmFields.crmNotConnectedDescription'));

  if (entityTypesLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
        <span className="ml-2 text-sm text-fg-muted">
          {t('crmFields.loading', 'Loading CRM field configuration...')}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* CRM Not Connected Alert */}
      {isCrmNotConnectedError && (
        <Alert variant="danger">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('crmFields.crmNotConnected', 'CRM Not Connected')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {t(
                'settings.crmFields.crmNotConnectedDescription',
                'Your CRM integration is not configured. Please connect your CRM system first.'
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => {
                // Data Source is the merged CRM/integrations settings section
                window.location.href = '/settings?section=data_source';
              }}
            >
              <ExternalLink className="h-3 w-3" />
              {t('crmFields.goToIntegrations', 'Go to Integrations')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* General Error Alert */}
      {lastError && !isCrmNotConnectedError && (
        <Alert variant="danger">
          <XCircle className="h-4 w-4" />
          <AlertTitle>
            {t('crmFields.refreshError', 'Failed to Refresh Fields')}
          </AlertTitle>
          <AlertDescription>
            <p className="text-sm">{lastError}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setLastError(null)}
            >
              {t('crmFields.dismissError', 'Dismiss')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Info banner */}
      <div className="p-3 bg-info-dim border border-border rounded-lg">
        <p className="text-sm text-fg">
          {t(
            'settings.crmFields.description',
            'CRM fields are cached locally for use in routing and delivery rules. Refresh to sync the latest fields from your CRM.'
          )}
        </p>
      </div>

      {/* Entity type cards */}
      <div className="space-y-3">
        {displayEntityTypes.map((entityType) => (
          <EntityTypeCard
            key={entityType.value}
            entityType={entityType.value}
            label={entityType.label}
            onRefresh={() => handleRefresh(entityType.value)}
            isRefreshing={refreshingType === entityType.value}
          />
        ))}
      </div>

      {/* Refresh all button */}
      <div className="flex justify-end pt-2">
        <Button
          variant="outline"
          onClick={async () => {
            for (const entityType of displayEntityTypes) {
              await handleRefresh(entityType.value);
            }
          }}
          disabled={refreshingType !== null}
          className="gap-2"
        >
          <RefreshCw
            className={cn('h-4 w-4', refreshingType !== null && 'animate-spin')}
          />
          {t('crmFields.refreshAll', 'Refresh All Fields')}
        </Button>
      </div>
    </div>
  );
}
